<template>
  <div class="feedback-form">
    <form
      v-if="!success"
      v-validity="validity"
      data-test-id="feedback-form-element"
      novalidate
      @submit.prevent="send"
    >
      <component
        :is="titleAs"
        v-if="title"
        class="feedback-form__title"
        data-test-id="feedback-form-title"
      >
        {{ title }}
      </component>
      <EField
        v-model="answer"
        name="answer"
        class="feedback-form__radios"
        data-test-id="feedback-form-answer"
        type="radio"
        required
        :options="options"
      />
      <EField
        v-if="subjects"
        v-model="subject"
        name="subject"
        class="feedback-form-select"
        data-test-id="feedback-form-subject"
        type="select"
        :force-stacked="true"
        :placeholder="subjectsPlaceholder"
        :options="subjects"
        required
      />
      <transition name="fade">
        <EField
          v-if="messageShown"
          v-model="message"
          name="message"
          type="textarea"
          class="feedback-form__textarea"
          data-test-id="feedback-form-message"
          maxlength="500"
          autoscale
          size="l"
          :force-stacked="true"
          :error="error"
          :placeholder="textareaPlaceholder"
        />
      </transition>
      <div class="feedback-form__actions">
        <ELoadingButton
          type="submit"
          class="feedback-form__submit"
          data-test-id="feedback-form-submit"
          variant="solid-primary"
          size="l"
          :loading="sending"
          :disabled="!validity.valid || sending"
        >
          {{ submitText }}
        </ELoadingButton>
      </div>
    </form>
    <div v-else class="feedback-form__success" data-test-id="feedback-form-success">
      <slot name="success">
        <EIcon
          icon="check-circle"
          size="l"
          class="feedback-form__success-icon"
          data-test-id="feedback-form-success-icon"
        />
        <h2 class="feedback-form__title" data-test-id="feedback-form-success-title">
          {{ successTitle }}
        </h2>
        <p class="feedback-form__success-message">
          {{ successMessage }}
        </p>
      </slot>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { getBreakpoint } from '~~/common/js/client/screen';
import '~~/common/vue/directives/validity';
import EField from '~~/common/vue/components/field';
import ELoadingButton from '~~/common/vue/components/loading-button';
import EIcon from '~~/common/vue/components/icon';

export const createData = () => ({
  message: '',
  answer: null,
  subject: null,
  error: '',
  sending: false,
  success: false,
  validity: {},
});

export default {
  name: 'EFeedbackForm',
  components: {
    EField,
    ELoadingButton,
    EIcon,
  },
  props: {
    errorMessage: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    query: {
      type: String,
      default: '',
    },
    subjectsPlaceholder: {
      type: String,
      default: '',
    },
    subjects: {
      type: Array,
      default: null,
    },
    submitText: {
      type: String,
      required: true,
    },
    successMessage: {
      type: String,
      required: true,
    },
    successTitle: {
      type: String,
      required: true,
    },
    textareaPlaceholder: {
      type: String,
      required: true,
    },
    ticketSubject: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    titleAs: {
      type: String,
      default: 'h2',
    },
    url: {
      type: String,
      required: true,
    },
  },
  data: createData,
  computed: {
    ...mapState('user', ['bingMarketCode']),
    ...mapState('browser', {
      browser: 'name',
      browserVersion: 'version',
    }),
    messageShown() {
      let shown = !!this.answer;
      if (this.subjects) {
        shown = !!this.subject;
      }
      return shown;
    }
  },
  methods: {
    resetData() {
      const data = createData();
      Object.assign(this.$data, data);
    },
    createTicket() {
      const { label: breakpoint } = getBreakpoint();
      const {
        answer,
        bingMarketCode,
        browser,
        browserVersion,
        message,
        query,
        subject,
        ticketSubject,
      } = this;
      const body = {
        answer,
        breakpoint,
        browser,
        browserVersion,
        market: bingMarketCode,
        message,
        query,
        subject,
      };
      const comment = { body: JSON.stringify(body, null, 2) };
      return {
        subject: `Feedback - ${ticketSubject}`,
        comment,
      };
    },
    async send() {
      if (this.sending) {
        return;
      }
      this.sending = true;
      const ticket = this.createTicket();
      const body = JSON.stringify({ ticket });
      try {
        const response = await fetch(this.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
        this.sending = false;
        this.success = response.ok;
        if (this.success) {
          this.$emit('success');
          return;
        }
        // using a generic error, instead of specific, to obfuscate internal info
        this.error = this.$t('common.header.feedback.error');
        // TODO: check why this is needed
      } catch (err) {
        this.sending = false;
        this.error = this.errorMessage;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.feedback-form {
  display: block;
  color: $color-text-primary;
}

.feedback-form__title {
  font-family: $family;
  font-size: $font-3l;
  font-weight: $font-weight-1l;
  margin-top: 0;
  margin-bottom: $space-l;
}

.feedback-form__textarea {
  min-height: $space-3l;
}

.feedback-form__actions {
  display: flex;
  justify-content: center;
  margin-top: $space-l;
}

.feedback-form__submit {
  width: auto;
}

.feedback-form__success {
  position: relative;
  text-align: center;
  overflow: auto;
  color: $color-text-primary;
}

.feedback-form__success-icon {
  margin: 0 0 $space-m;
  font-family: $family;
  font-size: $font-l;
  height: $space-4l;
  width: $space-4l;
  stroke-width: 5px;
  color: $color-brand-primary;
}

.feedback-form__success-message {
  font-family: $family;
  font-size: $font-l;
  margin: $space-s 0 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity $timing-1s $easing;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<docs>
A Feedback Form

### Configuring the endpoint

Use the `url` property to define the desired endpoint.
We usually use [Mercury proxy](https://github.com/ecosia/core/tree/master/mercury).

### States

#### Default
```js
const options = ['I like the feature', 'Something is missing',
  'I don\'t find this useful', 'I don\'t understand this'];

<div>
  <EFeedbackForm
    success-title="Thank you for your time!"
    success-message="Your feedback will help us improve ecosia"
    textarea-placeholder="Tell us more (optional)"
    submit-text="Send"
    :options="options"
    url="https://httpstat.us/200"
    error-message="Ops, there was an error sending feedback"
  />
</div>
```

#### With subjects
```js
const options = ['I like the feature', 'Something is missing',
  'I don\'t find this useful', 'I don\'t understand this'];
const subjects = ['Log-in feature', 'Personal counter', 'General']
<div>
  <EFeedbackForm
    success-title="Thank you for your time?"
    success-message="Your feedback will help us improve ecosia"
    textarea-placeholder="Tell us more (optional)"
    subjects-placeholder="please choose an option"
    submit-text="Send"
    :subjects="subjects"
    :options="options"
    url="https://httpstat.us/200"
    error-message="Ops, there was an error sending feedback"
  />
</div>
```

#### Ticket options
The feedback ticket generated by the form has a subject line that can be changed.
subject will be "Feedback -- Just wanted to say hello!"

```js
const options = ['I like the feature', 'Something is missing',
  'I don\'t find this useful', 'I don\'t understand this'];
const subjects = ['Log-in feature', 'Personal counter', 'General']
<div>
  <EFeedbackForm
    success-title="Thank you for your time?"
    success-message="Your feedback will help us improve ecosia"
    textarea-placeholder="Tell us more (optional)"
    submit-text="Send"
    :options="options"
    url="https://httpstat.us/200"
    error-message="Ops, there was an error sending feedback"
    ticket-subject="Just wanted to say hello!"
  />
</div>
```
</docs>
